import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import Box from '@mui/material/Box';

import { AppImage } from '@/components/atoms';
import { type ImageZoom } from '@/hooks/useImageZoom';
import { type SwipeNavigation } from '@/hooks/useSwipeNavigation';

import { type ViewerImage } from './types';
import { ViewerIconButton } from './ViewerIconButton';

type Props = {
  image: ViewerImage;
  zoom: ImageZoom;
  swipe: SwipeNavigation;
};

/**
 * The photograph itself, and the one gesture that acts on it.
 *
 * ## Fitted, then enlarged in layout
 *
 * Unzoomed the image is `contain`ed in the stage: the whole photograph, never
 * cropped, because a gallery that crops is a gallery that hides the thing
 * somebody opened it to see.
 *
 * Zoomed, it is drawn *larger* inside a scrollable box rather than
 * transformed. That is the whole trick: panning is then the browser's own
 * scrolling, with its own momentum, its own bounds and its own two-finger
 * pinch still working on top — none of which has to be written, and all of
 * which a `transform` rig would have had to replace.
 *
 * ## `svh`, not `vh`
 *
 * `vh` on a phone is the viewport with the browser's address bar hidden, which
 * it is not when the viewer opens — so a `vh`-sized stage puts the bottom of
 * every photograph behind the chrome until the first scroll. `svh` is the
 * small viewport, which is the one that is actually there.
 */
export function LightboxStage({ image, zoom, swipe }: Props) {
  const { zoomed, scale } = zoom;

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'grid',
        placeItems: 'center',
        // Scrollable only once there is something to pan to, so an unzoomed
        // stage cannot be nudged around by a stray swipe.
        overflow: zoomed ? 'auto' : 'hidden',
        // The browser keeps the pinch; the swipe handler keeps horizontal
        // flicks only while there is nothing to pan.
        touchAction: zoomed ? 'pan-x pan-y pinch-zoom' : 'pinch-zoom',
        cursor: zoomed ? 'zoom-out' : 'zoom-in',
        px: { xs: 0, md: 10 },
      }}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClick={zoom.toggle}
    >
      <AppImage
        // Keyed by id so React replaces the element on a page rather than
        // mutating its `src`: a mutated `<img>` keeps the previous picture on
        // screen until the new one decodes, which reads as the arrow key
        // having done nothing.
        key={image.id}
        src={image.src}
        alt={image.alt}
        width={image.width ?? 1600}
        height={image.height ?? 1067}
        priority
        sizes={zoomed ? '250vw' : '100vw'}
        style={{
          display: 'block',
          width: zoomed ? `${scale * 100}%` : 'auto',
          maxWidth: zoomed ? 'none' : '100%',
          height: 'auto',
          maxHeight: zoomed ? 'none' : '82svh',
          objectFit: 'contain',
        }}
      />

      <Box sx={{ position: 'absolute', right: { xs: 12, md: 20 }, bottom: { xs: 12, md: 20 } }}>
        <ViewerIconButton
          label={zoomed ? 'Zoom out' : 'Zoom in'}
          icon={zoomed ? <ZoomOut /> : <ZoomIn />}
          onClick={(event) => {
            // The stage itself toggles zoom on click; without this the control
            // would toggle it twice and land back where it started.
            event.stopPropagation();
            zoom.toggle();
          }}
        />
      </Box>
    </Box>
  );
}
